import { CSSProperties, ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
    appTitle,
    boolAndReducer,
    getDatefromWeekNum,
    getDatefromWeekNum0,
    MyDate,
    myAxios,
    projectCategoryColorCode,
    round,
    Select,
    simpleStatusColorCode,
    weeknum,
} from "src/components/common/functions";
import Export from "./export";

export type ProjectCalendarAssignmentStatus = "Created" | "Submitted" | "Approved" | "Rejected";

export type ProjectCalendarRow = {
    projectId: string;
    projectNumber: string;
    organizationName: string;
    clientProjectNumber: string;
    category: string;
    workId: string;
    assignmentId: string;
    assignmentStatus: ProjectCalendarAssignmentStatus | string;
    billed: string;
    userName: string;
    laborCategory: string;
    truckNumber?: string;
    travelStartTime: string;
    workStartTime: string;
    workEndTime: string;
    travelEndTime: string;
    startTime: string;
    endTime: string;
    preDriver?: string;
    hadLunch?: string;
    postDriver?: string;
    projectName?: string;
    portal?: number | string;
    withoutTravel?: number | string;
};

type ProjectCalendarProps = {
    tabTitle: string;
};

type WeekRangeResponse = {
    minTime: string;
    maxTime: string;
};

type HoursSummary = {
    portal: number | string;
    withoutTravel: number | string;
};

type CalendarView = "month" | "week" | "day";

type UserOption = {
    label: string;
    value: string;
};

type ProjectCalendarAssignmentView = ProjectCalendarRow & {
    projectName: string;
    userName: string;
    startDate: Date;
    endDate: Date;
    displayStart: string;
    displayEnd: string;
    portal: number | string;
    withoutTravel: number | string;
};

type TimedSegment = {
    assignment: ProjectCalendarAssignmentView;
    top: number;
    height: number;
    startsBefore: boolean;
    endsAfter: boolean;
};

const title = "ProjectCalendar";
const allowedViews = new Set(["month", "week", "day"]);
const dayNamesShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const dayNamesLong = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const statusLegend = [
    { key: "created", label: "Unsubmitted", className: "calendarLegendChipUnsubmitted" },
    { key: "submitted", label: "Submitted", className: "calendarLegendChipSubmitted" },
    { key: "approved", label: "Approved", className: "calendarLegendChipApproved" },
    { key: "billed", label: "Billed", className: "calendarLegendChipBilled" },
    { key: "rejected", label: "Rejected", className: "calendarLegendChipRejected" },
] as const;
const hourLabels = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: formatHourLabel(hour),
}));
const hourHeight = 64;
const minimumEventHeight = 28;

export default function ProjectCalendar({ tabTitle }: ProjectCalendarProps) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { week } = useParams<{ week?: string }>();
    const currentWeek = String(weeknum(new MyDate()));

    const [rawData, setRawData] = useState<ProjectCalendarRow[]>([]);
    const [showLoading, setShowLoading] = useState(true);
    const [weeks, setWeeks] = useState<string[]>([]);

    const requestedView = searchParams.get("view") ?? "week";
    const view = (allowedViews.has(requestedView) ? requestedView : "week") as CalendarView;
    const fallbackWeek = week ?? currentWeek;
    const fallbackDate = getDatefromWeekNum(fallbackWeek, 0) ?? new MyDate().s();
    const requestedDate = normalizeDate(searchParams.get("date")) ?? fallbackDate;
    const activeDate = requestedDate;
    const selectedWeek = String(weeknum(activeDate));

    const status = searchParams.get("status") ?? "All";
    const showTimes = searchParams.get("showTimes") === "1";
    const showHours = searchParams.get("showHours") === "1";
    const category = searchParams.get("category") ?? "All";
    const filterWords = searchParams.get("q") ?? "";
    const selectedUsers = useMemo(
        () => (searchParams.get("users") ?? "").split(",").map(value => value.trim()).filter(Boolean),
        [searchParams]
    );

    const categories = Object.keys(projectCategoryColorCode);

    const getHours = (assignment: ProjectCalendarRow): HoursSummary => {
        if (assignment.assignmentStatus === "Created") {
            return { portal: 0, withoutTravel: 0 };
        }
        const travelStartTime = new Date(assignment.travelStartTime.replace(/-/g, "/"));
        const workStartTime = new Date(assignment.workStartTime.replace(/-/g, "/"));
        const workEndTime = new Date(assignment.workEndTime.replace(/-/g, "/"));
        const travelEndTime = new Date(assignment.travelEndTime.replace(/-/g, "/"));
        const workHours = (workEndTime.getTime() - workStartTime.getTime()) / 3600000;
        const travel = (travelEndTime.getTime() - travelStartTime.getTime()) / 3600000;

        if (travelEndTime.toString() === "Invalid Date") {
            return { portal: "Invalid Date", withoutTravel: "Invalid Date" };
        }
        return {
            portal: round(travel, 2),
            withoutTravel: round(workHours, 2),
        };
    };

    const normalizedAssignments = useMemo<ProjectCalendarAssignmentView[]>(() => rawData.map(raw => {
        const projectName = `${raw.projectNumber} - ${raw.organizationName} - ${raw.clientProjectNumber}`;
        const userName = raw.userName.replace(/ {2}/g, " ");
        const startDate = new Date(raw.startTime);
        const parsedEndDate = new Date(raw.endTime);
        const endDate = parsedEndDate.getTime() > startDate.getTime()
            ? parsedEndDate
            : new Date(startDate.getTime() + 60 * 60 * 1000);
        return {
            ...raw,
            ...getHours(raw),
            projectName,
            userName,
            startDate,
            endDate,
            displayStart: formatClockTime(startDate),
            displayEnd: formatClockTime(endDate),
        };
    }), [rawData]);

    const userOptions = useMemo<UserOption[]>(() => Array.from(
        new Set(normalizedAssignments.map(assignment => assignment.userName).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b)).map(userName => ({
        label: userName,
        value: userName,
    })), [normalizedAssignments]);

    const selectedUserOptions = useMemo(
        () => userOptions.filter(option => selectedUsers.includes(option.value)),
        [selectedUsers, userOptions]
    );

    const filteredAssignments = useMemo(() => normalizedAssignments.filter(assignment =>
        ((status !== "All")
            ? (
                status === "Unsubmitted"
                    ? assignment.assignmentStatus === "Created"
                    : status === "Billed"
                        ? assignment.billed === "Yes"
                        : assignment.assignmentStatus === status
            )
            : true) &&
        assignment.laborCategory !== "Images" &&
        (selectedUsers.length ? selectedUsers.includes(assignment.userName) : true) &&
        (category !== "All" ? assignment.category === category : true) &&
        filterWords.toLowerCase().split(" ").map(word => JSON.stringify(
            [assignment.userName, assignment.projectName, assignment.projectNumber, assignment.truckNumber]
        ).toLowerCase().indexOf(word) > -1).reduce(boolAndReducer, true)
    ), [normalizedAssignments, status, selectedUsers, category, filterWords]);

    const summary = {
        created: filteredAssignments.filter(value => value.assignmentStatus === "Created").length,
        submitted: filteredAssignments.filter(value => value.assignmentStatus === "Submitted").length,
        approved: filteredAssignments.filter(value => value.assignmentStatus === "Approved").length,
        billed: filteredAssignments.filter(value => value.billed === "Yes").length,
        rejected: filteredAssignments.filter(value => value.assignmentStatus === "Rejected").length,
    };

    const activeDateObject = useMemo(() => new MyDate(activeDate.replace(/-/g, "/")), [activeDate]);
    const weekDates = useMemo(() => Array.from({ length: 7 }, (_, index) => getDatefromWeekNum(selectedWeek, index) ?? ""), [selectedWeek]);
    const visibleDates = useMemo(() => (view === "day" ? [activeDate] : view === "week" ? weekDates : []), [activeDate, view, weekDates]);
    const monthGridDates = useMemo(() => buildMonthGrid(activeDateObject), [activeDateObject]);

    const allDaySegments = useMemo(() => {
        const result: Record<string, ProjectCalendarAssignmentView[]> = {};
        visibleDates.forEach(date => {
            result[date] = filteredAssignments.filter(assignment => isAllDayAssignment(assignment) && intersectsDate(assignment, date));
        });
        return result;
    }, [filteredAssignments, visibleDates]);

    const timedSegments = useMemo(() => {
        const result: Record<string, TimedSegment[]> = {};
        visibleDates.forEach(date => {
            result[date] = filteredAssignments
                .filter(assignment => !isAllDayAssignment(assignment))
                .map(assignment => getTimedSegment(assignment, date))
                .filter((segment): segment is TimedSegment => Boolean(segment))
                .sort((a, b) => a.top - b.top || b.height - a.height);
        });
        return result;
    }, [filteredAssignments, visibleDates]);

    const monthAssignmentsByDate = useMemo(() => {
        const result: Record<string, ProjectCalendarAssignmentView[]> = {};
        monthGridDates.forEach(date => {
            result[date] = filteredAssignments
                .filter(assignment => intersectsDate(assignment, date))
                .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        });
        return result;
    }, [filteredAssignments, monthGridDates]);

    useEffect(() => {
        let isMounted = true;
        const getWeeks = async () => {
            const resWeeks = await myAxios({ fileName: "projectCalendar/readLaborAssignmentsWeek.php" }) as { data: WeekRangeResponse } | undefined;
            if (!isMounted || !resWeeks) return;

            const dateArray: string[] = [];
            const dataMin = new MyDate(resWeeks.data.minTime.replace(/-/g, "/"));
            const dataMax = new MyDate(resWeeks.data.maxTime.replace(/-/g, "/"));
            while (dataMin < dataMax) {
                dateArray.push(String(weeknum(dataMin)));
                dataMin.setDate(dataMin.getDate() + 7);
            }
            const maxWeek = String(weeknum(dataMax));
            if (!dateArray.includes(maxWeek)) {
                dateArray.push(maxWeek);
            }
            setWeeks(dateArray.sort((a, b) => (a < b ? 1 : -1)));
        };
        void getWeeks();
        return () => {
            isMounted = false;
        };
    }, [currentWeek]);

    useEffect(() => {
        if (!weeks.length) {
            return;
        }

        if (weeks.includes(selectedWeek)) {
            return;
        }

        const nextWeek = weeks[0];
        const nextDate = getDatefromWeekNum(nextWeek, 0) ?? activeDate;
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("date", nextDate);
        nextParams.set("view", view);
        navigate(`/${title}/${nextWeek}?${nextParams.toString()}`, { replace: true });
    }, [activeDate, navigate, searchParams, selectedWeek, view, weeks]);

    useEffect(() => {
        const nextParams = new URLSearchParams(searchParams);
        let shouldNavigate = false;

        if (requestedView !== view) {
            nextParams.set("view", view);
            shouldNavigate = true;
        }
        if (searchParams.get("date") !== activeDate) {
            nextParams.set("date", activeDate);
            shouldNavigate = true;
        }

        const targetWeek = selectedWeek;
        if ((!weeks.length || weeks.includes(targetWeek)) && (!week || week !== targetWeek || shouldNavigate)) {
            navigate(`/${title}/${targetWeek}?${nextParams.toString()}`, { replace: true });
        }
    }, [activeDate, navigate, requestedView, searchParams, selectedWeek, view, week, weeks]);

    useEffect(() => {
        let isMounted = true;
        const getData = async () => {
            const { rangeStart, rangeEnd } = getRangeFromView(view, activeDate, selectedWeek);
            const formData = new FormData();
            formData.append("weekStart", rangeStart);
            formData.append("weekEnd", rangeEnd);
            setShowLoading(true);
            const res = await myAxios({ fileName: "projectCalendar/readLaborAssignments.php", formData }) as { data: ProjectCalendarRow[] } | undefined;
            if (!isMounted) return;
            setShowLoading(false);
            if (!res) return;

            setRawData((res.data ?? []).map(raw => ({
                ...raw,
                travelStartTime: normalizeDateTime(raw.travelStartTime),
                workStartTime: normalizeDateTime(raw.workStartTime),
                workEndTime: normalizeDateTime(raw.workEndTime),
                travelEndTime: normalizeDateTime(raw.travelEndTime),
                startTime: normalizeDateTime(raw.startTime),
                endTime: normalizeDateTime(raw.endTime),
            })));
        };
        void getData();
        return () => {
            isMounted = false;
        };
    }, [activeDate, selectedWeek, view]);

    useEffect(() => {
        document.title = tabTitle;
        return () => {
            document.title = appTitle;
        };
    }, [tabTitle]);

    const updateParam = (key: string, value: string) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set(key, value);
        setSearchParams(nextParams);
    };

    const navigateToState = (nextDate: string, nextView: CalendarView = view) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("date", nextDate);
        nextParams.set("view", nextView);
        navigate(`/${title}/${weeknum(nextDate)}?${nextParams.toString()}`);
    };

    const navigateByOffset = (offset: number) => {
        const nextDate = shiftDate(activeDate, view === "month" ? { months: offset } : { days: view === "week" ? offset * 7 : offset });
        navigateToState(nextDate);
    };

    return (
        <div className = "calendarPage">
            <section className = "calendarToolbar">
                <div className = "calendarUserRow">
                    <Field label = "Users" htmlFor = "projectCalendarUsers">
                        <Select
                            inputId = "projectCalendarUsers"
                            classNamePrefix = "custom-select"
                            isMulti = {true}
                            isClearable = {true}
                            placeholder = "All users"
                            options = {userOptions}
                            value = {selectedUserOptions}
                            onChange = {(next: UserOption[] | null) => {
                                const nextParams = new URLSearchParams(searchParams);
                                nextParams.set("users", (next ?? []).map(option => option.value).join(","));
                                setSearchParams(nextParams);
                            }}
                        />
                    </Field>
                </div>

                <div className = "calendarToolbarGrid">
                    <Field label = "Search" htmlFor = "projectCalendarSearch">
                        <input
                            id = "projectCalendarSearch"
                            className = "form-control"
                            type = "text"
                            value = {filterWords}
                            onChange = {event => updateParam("q", event.currentTarget.value.toLowerCase())}
                        />
                    </Field>
                    <Field label = "Status" htmlFor = "projectCalendarStatus">
                        <select
                            id = "projectCalendarStatus"
                            className = "form-control"
                            value = {status}
                            onChange = {event => updateParam("status", event.currentTarget.value)}
                        >
                            <option>All</option>
                            <option>Unsubmitted</option>
                            <option>Submitted</option>
                            <option>Approved</option>
                            <option>Rejected</option>
                            <option>Billed</option>
                        </select>
                    </Field>
                    <Field label = "Category" htmlFor = "projectCalendarCategory">
                        <select
                            id = "projectCalendarCategory"
                            className = "form-control"
                            value = {category}
                            onChange = {event => updateParam("category", event.currentTarget.value)}
                        >
                            <option>All</option>
                            {categories.map((option, key) => <option key = {key}>{option}</option>)}
                        </select>
                    </Field>
                </div>

                <div className = "calendarToolbarFooter">
                    <ToggleField label = "Show Times" htmlFor = "projectCalendarShowTimes">
                        <input
                            id = "projectCalendarShowTimes"
                            type = "checkbox"
                            className = "cssCheckBox"
                            checked = {showTimes}
                            onChange = {event => updateParam("showTimes", event.currentTarget.checked ? "1" : "0")}
                        />
                    </ToggleField>
                    <ToggleField label = "Show Hours" htmlFor = "projectCalendarShowHours">
                        <input
                            id = "projectCalendarShowHours"
                            type = "checkbox"
                            className = "cssCheckBox"
                            checked = {showHours}
                            onChange = {event => updateParam("showHours", event.currentTarget.checked ? "1" : "0")}
                        />
                    </ToggleField>
                    <div className = "payrollExportWrap">
                        <Export filteredData = {filteredAssignments} week = {selectedWeek} />
                    </div>
                </div>
            </section>

            <section className = "calendarLegend">
                {statusLegend.map(item => (
                    <div className = {`calendarLegendChip ${item.className}`} key = {item.key}>
                        <span>{item.label}</span>
                        <strong>{summary[item.key]}</strong>
                    </div>
                ))}
            </section>

            <div className = "calendarNavInputs">
                <div className = "calendarViewSwitch" role = "tablist" aria-label = "Project calendar views">
                    {(["month", "week", "day"] as CalendarView[]).map(option => (
                        <button
                            key = {option}
                            className = {`calendarViewButton${view === option ? " active" : ""}`}
                            type = "button"
                            onClick = {() => navigateToState(activeDate, option)}
                        >
                            {capitalize(option)}
                        </button>
                    ))}
                </div>
                {view === "month" ? (
                    <Field htmlFor = "projectCalendarMonth">
                        <select
                            id = "projectCalendarMonth"
                            className = "form-control"
                            value = {activeDate.slice(0, 7)}
                            onChange = {event => navigateToState(`${event.currentTarget.value}-01`)}
                        >
                            {getMonthOptions(weeks).map((value, index) => (
                                <option key = {index} value = {value}>{formatMonthOption(value)}</option>
                            ))}
                        </select>
                    </Field>
                ) : null}
                {view === "week" ? (
                    <Field htmlFor = "projectCalendarWeek">
                        <select
                            id = "projectCalendarWeek"
                            className = "form-control"
                            value = {selectedWeek}
                            onChange = {event => {
                                const nextDate = getDatefromWeekNum(event.currentTarget.value, 0) ?? activeDate;
                                navigateToState(nextDate);
                            }}
                        >
                            {weeks.map((value, index) => <option key = {index}>{value}</option>)}
                        </select>
                    </Field>
                ) : null}
                {view === "day" ? (
                    <Field htmlFor = "projectCalendarDate">
                        <input
                            id = "projectCalendarDate"
                            className = "form-control"
                            type = "date"
                            value = {activeDate}
                            onChange = {event => navigateToState(event.currentTarget.value)}
                        />
                    </Field>
                ) : null}
                <div className = "calendarNavButtons">
                    <button className = "calendarNavButton" type = "button" onClick = {() => navigateByOffset(-1)}>Prev</button>
                    <button className = "calendarNavButton" type = "button" onClick = {() => navigateByOffset(1)}>Next</button>
                </div>
            </div>

            {showLoading ? <div className = "cssLoading">Loading...</div> : (
                view === "month" ? (
                    <MonthView
                        activeDate = {activeDate}
                        monthGridDates = {monthGridDates}
                        assignmentsByDate = {monthAssignmentsByDate}
                        onSelectDate = {date => navigateToState(date, "day")}
                        showTimes = {showTimes}
                        showHours = {showHours}
                    />
                ) : (
                    <TimedView
                        view = {view}
                        visibleDates = {visibleDates}
                        allDaySegments = {allDaySegments}
                        timedSegments = {timedSegments}
                        showTimes = {showTimes}
                        showHours = {showHours}
                        onSelectDate = {date => navigateToState(date, "day")}
                    />
                )
            )}
        </div>
    );
}

function TimedView({
    view,
    visibleDates,
    allDaySegments,
    timedSegments,
    showTimes,
    showHours,
    onSelectDate,
}: {
    view: CalendarView;
    visibleDates: string[];
    allDaySegments: Record<string, ProjectCalendarAssignmentView[]>;
    timedSegments: Record<string, TimedSegment[]>;
    showTimes: boolean;
    showHours: boolean;
    onSelectDate: (date: string) => void;
}) {
    const gridHeight = hourHeight * 24;

    return (
        <section className = "calendarTimedShell">
            <div className = "calendarAllDayRow">
                <div className = "calendarTimeAxisSpacer">All day</div>
                <div className = {`calendarAllDayColumns cols-${visibleDates.length}`}>
                    {visibleDates.map((date, index) => (
                        <div className = "calendarAllDayColumn" key = {date}>
                            <button className = "calendarTimedDayHeader" type = "button" onClick = {() => onSelectDate(date)}>
                                <span>{view === "week" ? dayNamesShort[index] : dayNamesLong[(new Date(date.replace(/-/g, "/"))).getDay() === 0 ? 6 : (new Date(date.replace(/-/g, "/"))).getDay() - 1]}</span>
                                <strong>{formatShortDate(date)}</strong>
                            </button>
                            <div className = "calendarAllDayEvents">
                                {allDaySegments[date]?.length ? allDaySegments[date].map(item => (
                                    <AssignmentPill key = {`${date}-${item.assignmentId}`} assignment = {item} showHours = {showHours} />
                                )) : <div className = "calendarNoAllDay">No all-day events</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className = "calendarTimedFrame">
                <div className = "calendarTimeAxis">
                    {hourLabels.map(row => (
                        <div className = "calendarTimeAxisRow" key = {row.hour} style = {{ height: hourHeight }}>
                            <span>{row.label}</span>
                        </div>
                    ))}
                </div>
                <div className = {`calendarTimedColumns cols-${visibleDates.length}`} style = {{ minHeight: gridHeight }}>
                    {visibleDates.map((date, index) => (
                        <div className = "calendarTimedColumn" key = {date}>
                            <div className = "calendarTimedColumnGrid">
                                {hourLabels.map(row => (
                                    <div className = "calendarTimedHourSlot" key = {row.hour} style = {{ height: hourHeight }} />
                                ))}
                            </div>
                            <div className = "calendarTimedEventsLayer">
                                {timedSegments[date]?.map(segment => (
                                    <TimedEventCard
                                        key = {`${date}-${segment.assignment.assignmentId}-${segment.top}`}
                                        segment = {segment}
                                        showTimes = {showTimes}
                                        showHours = {showHours}
                                    />
                                ))}
                                {!timedSegments[date]?.length && !allDaySegments[date]?.length ? (
                                    <div className = "calendarTimedEmpty">No assignments</div>
                                ) : null}
                            </div>
                            {view === "week" && index < visibleDates.length - 1 ? <div className = "calendarTimedColumnDivider" /> : null}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function MonthView({
    activeDate,
    monthGridDates,
    assignmentsByDate,
    onSelectDate,
    showTimes,
    showHours,
}: {
    activeDate: string;
    monthGridDates: string[];
    assignmentsByDate: Record<string, ProjectCalendarAssignmentView[]>;
    onSelectDate: (date: string) => void;
    showTimes: boolean;
    showHours: boolean;
}) {
    const activeMonth = activeDate.slice(0, 7);

    return (
        <section className = "calendarMonthView">
            <div className = "calendarMonthHeader">
                {dayNamesShort.map(day => <div className = "calendarMonthHeaderCell" key = {day}>{day}</div>)}
            </div>
            <div className = "calendarMonthGrid">
                {monthGridDates.map(date => {
                    const items = assignmentsByDate[date] ?? [];
                    const inMonth = date.slice(0, 7) === activeMonth;
                    return (
                        <div className = {`calendarMonthCell${inMonth ? "" : " outside"}${date === activeDate ? " active" : ""}`} key = {date}>
                            <button className = "calendarMonthDateButton" type = "button" onClick = {() => onSelectDate(date)}>
                                <div className = "calendarMonthDateRow">
                                    <span>{date.slice(8, 10)}</span>
                                    <strong>{items.length || ""}</strong>
                                </div>
                            </button>
                            <div className = "calendarMonthAssignments">
                                {items.slice(0, 3).map(item => (
                                    <Link className = "calendarMonthAssignment calendarMonthAssignmentLink" to = {`/Assignments/${item.assignmentId}`} key = {`${date}-${item.assignmentId}`}>
                                        <span className = "calendarMonthAssignmentDot" style = {{ backgroundColor: getStatusColor(item.assignmentStatus, item.billed) }} />
                                        <span className = "calendarMonthAssignmentText">
                                            {showTimes && !isAllDayAssignment(item) ? `${item.displayStart} ` : ""}
                                            {item.projectNumber} • {item.userName}
                                        </span>
                                        {showHours && item.assignmentStatus !== "Created" ? (
                                            <span className = "calendarMonthAssignmentHours">{String(item.withoutTravel)}h</span>
                                        ) : null}
                                    </Link>
                                ))}
                                {items.length > 3 ? <div className = "calendarMonthMore">+{items.length - 3} more</div> : null}
                                {!items.length ? <div className = "calendarMonthEmpty">No assignments</div> : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function AssignmentPill({ assignment, showHours }: { assignment: ProjectCalendarAssignmentView; showHours: boolean }) {
    return (
        <Link className = "calendarAllDayEvent" to = {`/Assignments/${assignment.assignmentId}`}>
            <span className = "calendarStatusDot" style = {{ backgroundColor: getStatusColor(assignment.assignmentStatus, assignment.billed) }} />
            <span className = "calendarAllDayText">{assignment.projectNumber} • {assignment.userName}</span>
            {showHours && assignment.assignmentStatus !== "Created" ? (
                <span className = "calendarAllDayHours">{String(assignment.withoutTravel)}h</span>
            ) : null}
        </Link>
    );
}

function TimedEventCard({
    segment,
    showTimes,
    showHours,
}: {
    segment: TimedSegment;
    showTimes: boolean;
    showHours: boolean;
}) {
    const { assignment } = segment;
    const style: CSSProperties = {
        top: segment.top,
        height: segment.height,
        borderLeftColor: getStatusColor(assignment.assignmentStatus, assignment.billed),
    };

    return (
        <Link className = "calendarTimedEvent" style = {style} to = {`/Assignments/${assignment.assignmentId}`}>
            <div className = "calendarTimedEventTitle">{assignment.projectNumber}</div>
            <div className = "calendarTimedEventProject">{assignment.userName}</div>
            <div className = "calendarTimedEventMeta">{assignment.projectName}</div>
            <div className = "calendarTimedEventMeta">{showTimes ? `${segment.startsBefore ? "..." : assignment.displayStart} - ${segment.endsAfter ? "..." : assignment.displayEnd}` : assignment.laborCategory}</div>
            <div className = "calendarTimedEventMeta">{assignment.laborCategory}{assignment.truckNumber ? ` • ${assignment.truckNumber}` : ""}</div>
            {showHours && assignment.assignmentStatus !== "Created" ? <div className = "calendarTimedEventMeta">Work {String(assignment.withoutTravel)}h • Travel {String(assignment.portal)}h</div> : null}
        </Link>
    );
}

function Field({ label, htmlFor, children }: { label?: string; htmlFor: string; children: ReactNode }) {
    return (
        <label className = "calendarField" htmlFor = {htmlFor}>
            {label ? <span className = "calendarFieldLabel">{label}</span> : null}
            {children}
        </label>
    );
}

function ToggleField({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
    return (
        <label className = "calendarToggle" htmlFor = {htmlFor}>
            <span className = "calendarToggleText">{label}</span>
            {children}
        </label>
    );
}

function normalizeDate(value: string | null) {
    if (!value) return null;
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function normalizeDateTime(value?: string | null) {
    return (value ?? "").replace(/-/g, "/");
}

function getRangeFromView(view: CalendarView, activeDate: string, selectedWeek: string) {
    if (view === "day") {
        const date = new MyDate(activeDate.replace(/-/g, "/"));
        return {
            rangeStart: date.s(),
            rangeEnd: new MyDate(date.getFullYear(), date.getMonth(), date.getDate() + 1).s(),
        };
    }
    if (view === "month") {
        const date = new MyDate(activeDate.replace(/-/g, "/"));
        return {
            rangeStart: new MyDate(date.getFullYear(), date.getMonth(), 1).s(),
            rangeEnd: new MyDate(date.getFullYear(), date.getMonth() + 1, 1).s(),
        };
    }
    return {
        rangeStart: getDatefromWeekNum0(selectedWeek, 0)!.toLocaleDateString("fr-CA", { timeZone: "America/Los_Angeles" }),
        rangeEnd: getDatefromWeekNum0(selectedWeek, 7)!.toLocaleDateString("fr-CA", { timeZone: "America/Los_Angeles" }),
    };
}

function isAllDayAssignment(item: ProjectCalendarAssignmentView) {
    return item.startTime.substring(11, 16) === "00:00" && item.endTime.substring(11, 16) === "00:00";
}

function intersectsDate(item: ProjectCalendarAssignmentView, date: string) {
    const dayStart = new Date(`${date}T00:00:00`);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    return item.endDate > dayStart && item.startDate < dayEnd;
}

function getTimedSegment(item: ProjectCalendarAssignmentView, date: string): TimedSegment | null {
    const dayStart = new Date(`${date}T00:00:00`);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    if (item.endDate <= dayStart || item.startDate >= dayEnd) return null;

    const segmentStart = item.startDate < dayStart ? dayStart : item.startDate;
    const segmentEnd = item.endDate > dayEnd ? dayEnd : item.endDate;
    const top = (((segmentStart.getHours() * 60) + segmentStart.getMinutes()) / 60) * hourHeight;
    const rawHeight = ((segmentEnd.getTime() - segmentStart.getTime()) / 3600000) * hourHeight;

    return {
        assignment: item,
        top,
        height: Math.max(minimumEventHeight, rawHeight),
        startsBefore: item.startDate < dayStart,
        endsAfter: item.endDate > dayEnd,
    };
}

function buildMonthGrid(activeDate: Date) {
    const firstOfMonth = new MyDate(activeDate.getFullYear(), activeDate.getMonth(), 1);
    const firstDayIndex = getMondayOffset(firstOfMonth.s());
    const gridStart = new MyDate(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - firstDayIndex);
    return Array.from({ length: 42 }, (_, index) => {
        const date = new MyDate(gridStart);
        date.setDate(gridStart.getDate() + index);
        return date.s();
    });
}

function getMondayOffset(dateString: string) {
    const weekday = new Date(dateString.replace(/-/g, "/")).getDay();
    return weekday === 0 ? 6 : weekday - 1;
}

function shiftDate(dateString: string, offset: { days?: number; months?: number }) {
    const date = new MyDate(dateString.replace(/-/g, "/"));
    if (offset.months) date.setMonth(date.getMonth() + offset.months);
    if (offset.days) date.setDate(date.getDate() + offset.days);
    return date.s();
}

function getMonthOptions(weeks: string[]) {
    return Array.from(new Set(weeks
        .map(curWeek => getDatefromWeekNum(curWeek, 0)?.slice(0, 7))
        .filter((value): value is string => Boolean(value))
    ));
}

function formatMonthOption(value: string) {
    return new Date(`${value}-01`.replace(/-/g, "/")).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
    });
}

function formatShortDate(date: string) {
    return new Date(date.replace(/-/g, "/")).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

function formatHourLabel(hour: number) {
    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";
    return `${hour - 12} PM`;
}

function formatClockTime(date: Date) {
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const suffix = hours >= 12 ? "PM" : "AM";
    const normalizedHour = hours % 12 || 12;
    return `${normalizedHour}:${minutes} ${suffix}`;
}

function capitalize(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function getStatusColor(status: ProjectCalendarRow["assignmentStatus"], billed: ProjectCalendarRow["billed"]) {
    if (billed === "Yes") return "pink";
    return simpleStatusColorCode[status as keyof typeof simpleStatusColorCode] ?? "black";
}
